import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, X } from "lucide-react";
import { motion, PanInfo } from "motion/react";
import { ITrack } from "@agp/shared.user-app/module/guide-player.module/audio-client";
import ServiceLocator from "../../../../domain/service-locator";
import { MonotoneGuideDetailThumbnail } from "./monotone-guide-detail-thumbnail";
import { useLanguageBasedFont } from "../const/font-utils";
import { twMerge } from "tailwind-merge";

type MonotoneFullAudioPlayerProps = {
  track: ITrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onNext?: () => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  opacity: number;
  pointerEvents: "auto" | "none";
  onClose?: () => void;
};

// フォントサイズの種類を定義
type FontSize = "small" | "large";

const MonotoneFullAudioPlayer: React.FC<MonotoneFullAudioPlayerProps> = ({
  track,
  isPlaying,
  currentTime,
  duration,
  onPlayPause,
  onSeek,
  onNext,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onMouseDown,
  opacity,
  pointerEvents,
  onClose,
}) => {
  // プログレスバー用の状態
  const [isSeekDragging, setIsSeekDragging] = useState(false);
  const [currentSeekRate, setCurrentSeekRate] = useState(0);
  // フォントサイズの状態を追加
  const [fontSize, setFontSize] = useState<FontSize>("small");
  const sliderRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const fontClass = useLanguageBasedFont();

  // ServiceLocatorから再生速度を取得
  const guidePlayerModule = ServiceLocator.GuidePlayerModule;
  const currentPlaybackRate = guidePlayerModule.playbackRate.watch();

  const clamp = (num: number, min: number, max: number) =>
    Math.min(Math.max(num, min), max);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // フォントサイズ切替関数
  const handleFontSizeChange = (size: FontSize) => {
    setFontSize(size);
  };

  // 再生速度変更のハンドラー
  const handleClickSpeedRateButton = useCallback(() => {
    switch (currentPlaybackRate) {
      case 1.0:
        guidePlayerModule.setPlaybackRate(1.25);
        return;
      case 1.25:
        guidePlayerModule.setPlaybackRate(1.5);
        return;
      case 1.5:
        guidePlayerModule.setPlaybackRate(2.0);
        return;
      case 2.0:
        guidePlayerModule.setPlaybackRate(1.0);
        return;
      default:
        return;
    }
  }, [currentPlaybackRate, guidePlayerModule]);

  // フォントサイズに応じたCSSクラスを取得
  const getTextSizeClass = () => {
    switch (fontSize) {
      case "small":
        return "text-xl";
      case "large":
        return "text-3xl";
      default:
        return "text-xl";
    }
  };

  // ボタンの背景色を取得
  const getButtonBgClass = (size: FontSize) => {
    return fontSize === size
      ? "bg-gray-100 text-black"
      : "bg-gray-[#F2F2F2] text-black";
  };

  // プログレスバーのドラッグ機能
  const getSeekRate = useCallback((clientX: number) => {
    const progressBarRect = progressBarRef.current?.getBoundingClientRect();
    if (!progressBarRect) return 0;
    return clamp(
      (clientX - progressBarRect.left) / progressBarRect.width,
      0,
      1
    );
  }, []);

  const onSeekDragStart = useCallback(
    (e: MouseEvent | TouchEvent | PointerEvent, _: PanInfo) => {
      e.preventDefault();
      e.stopPropagation();
      setIsSeekDragging(true);
    },
    []
  );

  const onSeekDrag = useCallback(
    (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      e.preventDefault();
      e.stopPropagation();
      const rate = getSeekRate(info.point.x);
      setCurrentSeekRate(rate);
      setIsSeekDragging(true);
    },
    [getSeekRate]
  );

  const onSeekDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const seekRate = getSeekRate(info.point.x);
      onSeek(duration * seekRate);
      setCurrentSeekRate(seekRate);
      setTimeout(() => {
        setIsSeekDragging(false);
      }, 100);
    },
    [getSeekRate, onSeek, duration]
  );

  // プログレスバーの位置を更新
  useEffect(() => {
    if (!isSeekDragging && duration !== 0) {
      const seekRate = currentTime / duration;
      if (seekRate === 0) {
        setCurrentSeekRate(0);
        return;
      }
      setCurrentSeekRate(seekRate);
      setCurrentSeekRate((x) => {
        const diffMilliseconds = Math.abs(x * duration - currentTime) * 1000;
        return diffMilliseconds < 500 ? seekRate : x;
      });
    }
  }, [duration, currentTime, isSeekDragging]);

  useEffect(() => {
    setCurrentSeekRate(0);
  }, [track]);

  // クリックでのシーク機能
  const handleProgressChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = progressBarRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    onSeek(Math.floor(newTime));
  };

  return (
    <>
      <div
        className={`overflow-y-auto hidden-scrollbar absolute inset-0 flex flex-col transition-opacity duration-300 ${fontClass}`}
        style={{
          opacity: opacity,
          pointerEvents: pointerEvents,
        }}
      >
        <div className={"w-full"}>
          <div
            className={"w-full bg-gray-200 flex items-start justify-center"}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
          >
            {track ? (
              track.thumbnailUrls.length > 0 ? (
                <img
                  src={track.thumbnailUrls[0]}
                  alt={`Track ${track.trackNumber} thumbnail`}
                  className="w-full h-auto"
                />
              ) : (
                <MonotoneGuideDetailThumbnail
                  trackNumber={track?.trackNumber || 1}
                  className="w-full h-[260px] max-w-md"
                />
              )
            ) : (
              <div className={"w-full h-[260px]"}></div>
            )}
          </div>
        </div>

        {/* エピソード情報 */}
        <div className={"p-6"}>
          <div className="text-left mb-6">
            <h3 className={`text-2xl mb-2 ${fontClass}`}>{track?.name}</h3>
          </div>

          {/* コントロールボタン */}
          <div className="flex justify-center items-center gap-8 mb-6">
            {/* ダミーボタン */}
            <button
              className={`opacity-0 p-3 w-[50px] h-[50px] rounded-full bg-[#F2F2F2] border-[1px] border-solid border-black ${fontClass}`}
            >
              next
            </button>
            <button
              className="p-4 rounded-full bg-[#F2F2F2] text-black border-[1px] border-solid border-black"
              onClick={onPlayPause}
            >
              {isPlaying ? (
                <Pause size={32} fill="black" />
              ) : (
                <Play size={32} fill="black" />
              )}
            </button>
            <button
              className={twMerge(
                `p-3 w-[50px] h-[50px] rounded-full bg-[#F2F2F2] border-[1px] border-solid border-black ${fontClass}`
              )}
              onClick={onNext}
              disabled={!onNext}
            >
              next
            </button>
          </div>

          {/* プログレスバー */}
          <div className="mb-4">
            {/* 透明なインプット領域（操作性向上のため） */}
            <div
              className="relative w-full py-3 cursor-pointer"
              ref={sliderRef}
            >
              {/* 実際のプログレスバー（見た目） */}
              <div
                className="relative w-full h-2 bg-gray-200 rounded-full"
                ref={progressBarRef}
              >
                <div
                  className="h-full bg-[#877F87] rounded-full transition-all duration-0"
                  style={{ width: `${currentSeekRate * 100}%` }}
                ></div>
              </div>

              {/* 透明なタッチ領域 */}
              <motion.div
                className="absolute inset-0 w-full h-full"
                drag="x"
                dragElastic={false}
                dragConstraints={progressBarRef}
                dragMomentum={false}
                onDragStart={onSeekDragStart}
                onDrag={onSeekDrag}
                onDragEnd={onSeekDragEnd}
                onClick={handleProgressChange}
              />
            </div>
            <div className="flex justify-between text-md text-gray-600 mt-2">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* 再生速度とフォントサイズ */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              <button
                className={`w-10 h-10 border border-black rounded-lg flex items-center justify-center text-lg transition-colors ${getButtonBgClass(
                  "small"
                )} ${fontClass}`}
                onClick={() => handleFontSizeChange("small")}
              >
                A
              </button>
              <button
                className={`w-10 h-10 border border-black rounded-lg flex items-center justify-center text-2xl transition-colors ${getButtonBgClass(
                  "large"
                )} ${fontClass}`}
                onClick={() => handleFontSizeChange("large")}
              >
                A
              </button>
            </div>
            <div
              className="text-lg border-[1px] border-black rounded-full px-2 py-1 cursor-pointer"
              onClick={handleClickSpeedRateButton}
            >
              ×{currentPlaybackRate.toFixed(2)}
            </div>
          </div>

          {/* 説明文 */}
          <div
            className={`w-full flex-1 break-all whitespace-pre-wrap ${getTextSizeClass()} text-[#4D4D4D]`}
          >
            {track?.text}
          </div>
        </div>
      </div>
      {/* 閉じるボタン */}
      <button
        className={"absolute top-5 right-5 p-2 text-gray-600 z-10"}
        style={{
          opacity: opacity,
          pointerEvents: pointerEvents,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
      >
        <X size={28} />
      </button>
    </>
  );
};

export default MonotoneFullAudioPlayer;
